import React from 'react';
import DOMPurify from 'dompurify';
import { FaInstagram, FaLinkedinIn, FaFacebookF, FaUser } from 'react-icons/fa';
import Logo from '../../Asset/logo.webp';
import './journal.css';

const safe = (html) => ({ __html: DOMPurify.sanitize(html || '') });

export function formatDate(d) {
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function hexTint(hex, alpha = 0.08) {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  } catch { return `rgba(39,69,89,${alpha})`; }
}

const SANS = '"Helvetica Neue", Arial, sans-serif';

const CALLOUT_STYLES = {
  info:    { bg: '#eff6ff', border: '#3b82f6', label: 'Information', icon: 'ℹ️' },
  tip:     { bg: '#f0fdf4', border: '#22c55e', label: 'Conseil',     icon: '💡' },
  warning: { bg: '#fffbeb', border: '#f59e0b', label: 'Attention',   icon: '⚠️' },
  success: { bg: '#f0fdf4', border: '#16a34a', label: 'À retenir',   icon: '✅' },
};

function bodyStyle(columns) {
  return {
    columnCount: columns,
    columnGap: '2.4rem',
    columnRule: '1px solid #e5e7eb',
    fontSize: '0.875rem',
    lineHeight: 1.85,
    textAlign: 'justify',
    color: '#1e293b',
    fontFamily: SANS,
  };
}

const CREAM = 'rgba(247,240,229,0.95)';

/* Mini-markdown : **gras**, *italique*, \n -> <br/> — utilisé pour rubans et libellés courts */
function miniMarkdown(text) {
  if (!text) return '';
  let t = String(text);
  t = t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/\*(.+?)\*/g, '<em>$1</em>');
  t = t.replace(/\n/g, '<br/>');
  return t;
}

/* ── Étiquette ruban (sommaire couverture) ── */
function RibbonTag({ text, tint = CREAM }) {
  return (
    <div
      style={{
        background: tint,
        padding: '9px 30px 9px 16px',
        fontSize: '0.66rem',
        fontWeight: 800,
        color: '#1e293b',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%)',
        maxWidth: '190px',
        lineHeight: 1.35,
        fontFamily: SANS,
        boxShadow: '2px 2px 6px rgba(0,0,0,0.12)',
      }}
      dangerouslySetInnerHTML={safe(miniMarkdown(text))}
    />
  );
}

/* ── Barre courante plate (style "NOS OUTILS | #HASHTAG | N°") ── */
function RunningBar({ label, hashtag, issueLabel, variant = 'header' }) {
  if (!label && !hashtag && !issueLabel) return null;
  const borderStyle = variant === 'header' ? { borderBottom: '1px solid #e5e7eb' } : { borderTop: '1px solid #e5e7eb' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 48px', background: '#fff', ...borderStyle }}>
      <span style={{ fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#0f172a', fontFamily: SANS, flex: 1 }}>{label || ''}</span>
      <span style={{ fontSize: '0.62rem', color: '#94a3b8', fontFamily: SANS, letterSpacing: '0.04em', flex: 1, textAlign: 'center' }}>{hashtag || ''}</span>
      <span style={{ fontSize: '0.66rem', fontWeight: 800, color: '#0f172a', fontFamily: SANS, letterSpacing: '0.04em', flex: 1, textAlign: 'right' }}>{issueLabel || ''}</span>
    </div>
  );
}

/* ── Petite icône sociale ronde ── */
function SocialCircle({ children }) {
  return (
    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '11px', color: '#334155' }}>
      {children}
    </div>
  );
}

/* ── Grille de points décorative ── */
function DotGrid({ color = 'rgba(255,255,255,0.55)' }) {
  const dots = Array.from({ length: 12 });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 6px)', gridTemplateRows: 'repeat(3, 6px)', gap: '5px', flexShrink: 0 }}>
      {dots.map((_, i) => <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: color }} />)}
    </div>
  );
}

/* ── Rangée d'avatars superposés (approximation des silhouettes illustrées) ── */
const AVATAR_COLORS = ['#cbd5e1', '#fca5a5', '#fcd34d', '#93c5fd'];
function AvatarRow({ count = 4 }) {
  return (
    <div style={{ display: 'flex', flexShrink: 0 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          width: '28px', height: '28px', borderRadius: '50%',
          background: AVATAR_COLORS[i % AVATAR_COLORS.length],
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginLeft: i === 0 ? 0 : '-8px', border: '2px solid #fff', color: '#fff', fontSize: '11px',
        }}>
          <FaUser />
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   BLOCS
══════════════════════════════════════════════════════ */
function Block({ block, defaultAccent, defaultColumns, meta = {} }) {
  const accent  = block.accentColor || defaultAccent;
  const columns = block.columnCount || defaultColumns;
  const tint    = hexTint(accent, 0.14);

  /* ── IMAGE ── */
  if (block.type === 'image') {
    const wMap = { full: '100%', half: '50%', third: '33%' };
    const w     = wMap[block.width] || '100%';
    const align = block.position === 'left' ? 'flex-start' : block.position === 'right' ? 'flex-end' : 'center';
    return (
      <figure className="journal-image" style={{ margin: 0, display: 'flex', flexDirection: 'column', alignItems: align }}>
        <img src={block.url} alt={block.alt || ''} style={{ width: w, maxWidth: '100%', display: 'block', borderRadius: '2px' }} />
        {(block.caption || block.author) && (
          <figcaption style={{ marginTop: '6px', fontSize: '0.68rem', color: '#94a3b8', textAlign: 'center', width: w, fontFamily: SANS }}>
            {block.caption}{block.author && <> — Photo : {block.author}</>}
          </figcaption>
        )}
      </figure>
    );
  }

  /* ── COVER IMAGE (overlay simple) ── */
  if (block.type === 'cover-image') {
    const hMap = { small: '160px', medium: '240px', large: '320px' };
    return (
      <div className="journal-cover-image" style={{ position: 'relative', height: hMap[block.height] || '240px', overflow: 'hidden', margin: 0 }}>
        <img src={block.url} alt={block.alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {(block.overlayTitle || block.overlayText) && (
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.05) 55%)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '20px 28px' }}>
            {block.overlayTitle && <h2 style={{ margin: '0 0 4px', color: '#fff', fontFamily: SANS, fontSize: '1.3rem', fontWeight: 900, lineHeight: 1.2, textTransform: 'uppercase' }}>{block.overlayTitle}</h2>}
            {block.overlayText  && <p  style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', fontFamily: SANS }}>{block.overlayText}</p>}
          </div>
        )}
      </div>
    );
  }

  /* ── FEATURE PHOTO — pleine page, bandeau titre + légende ── */
  if (block.type === 'feature-photo') {
    const hMap = { small: '220px', medium: '320px', large: '420px', full: '540px' };
    return (
      <div className="journal-feature-photo" style={{ position: 'relative', margin: '0 -48px', height: hMap[block.height] || '320px', overflow: 'hidden' }}>
        <img src={block.url} alt={block.alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: block.grayscale ? 'grayscale(1)' : 'none' }} />
        {block.label && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: 'rgba(110,110,110,0.58)', padding: '16px 24px', textAlign: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem', letterSpacing: '0.04em', fontFamily: SANS, textTransform: 'uppercase' }}>— {block.label} —</span>
          </div>
        )}
        {block.caption && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72), transparent)', padding: '50px 32px 22px', textAlign: 'center' }}>
            <span style={{ color: '#fff', fontSize: '0.88rem', letterSpacing: '0.02em', fontFamily: SANS, fontWeight: 500 }}>{block.caption}</span>
          </div>
        )}
      </div>
    );
  }

  /* ── TOC COVER — sommaire en rubans sur photo + bandeau dossier ── */
  if (block.type === 'toc-cover') {
    const items = Array.isArray(block.items) ? block.items.filter(Boolean) : [];
    return (
      <div className="journal-toc-cover" style={{ margin: '0 -48px' }}>
        <div style={{ position: 'relative', height: block.photoHeight ? { small: '260px', medium: '360px', large: '460px' }[block.photoHeight] : '360px', overflow: 'hidden', background: '#e5e7eb' }}>
          {block.photoUrl && <img src={block.photoUrl} alt={block.photoAlt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
          {items.length > 0 && (
            <div style={{ position: 'absolute', left: 0, top: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {items.map((it, i) => <RibbonTag key={i} text={it} />)}
            </div>
          )}
        </div>
        {(block.dossierLabel || block.dossierSubtitle) && (
          <div style={{ background: accent, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 48px', gap: '16px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              <img src={Logo} alt="HIS" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
            </div>
            <div style={{ textAlign: 'right' }}>
              {block.dossierLabel    && <div style={{ color: '#fff', fontWeight: 900, fontSize: '0.85rem', letterSpacing: '0.08em', fontFamily: SANS, textTransform: 'uppercase' }}>— {block.dossierLabel} —</div>}
              {block.dossierSubtitle && <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.68rem', letterSpacing: '0.04em', fontFamily: SANS, marginTop: '3px', textTransform: 'uppercase' }}>— {block.dossierSubtitle} —</div>}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ── SECTION DIVIDER — page d'ouverture de rubrique ── */
  if (block.type === 'section-divider') {
    const label     = block.runningLabel || meta.runningLabel || block.label || '';
    const hashtag    = block.hashtag    || meta.hashtag    || '';
    const issueLabel = block.issueLabel || meta.issueLabel || '';
    return (
      <div className="journal-section-divider" style={{ margin: '0 -48px' }}>
        {/* Logo en haut à gauche, comme la page d'ouverture de rubrique de référence */}
        <div style={{ padding: '24px 48px 0' }}>
          <div style={{ width: '46px', height: '46px', borderRadius: '50%', overflow: 'hidden', background: '#fff', border: `1px solid ${hexTint(accent, 0.3)}` }}>
            <img src={Logo} alt="HIS" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
        </div>

        <div style={{ padding: '36px 48px 44px', textAlign: 'center' }}>
          {block.label && <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.22em', color: accent, textTransform: 'uppercase', marginBottom: '22px', fontFamily: SANS }}>{block.label}</div>}
          {block.iconUrl && <img src={block.iconUrl} alt="" style={{ height: '88px', objectFit: 'contain', margin: '0 auto 18px', display: 'block' }} />}
          {block.title && <div style={{ fontSize: '1.7rem', fontWeight: 900, color: '#0f172a', fontFamily: SANS, letterSpacing: '0.01em' }}>{block.title}</div>}
          {block.subtitle && <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '8px', fontFamily: SANS }}>{block.subtitle}</div>}
        </div>

        {/* Pied courant — reproduit le bandeau "NOS OUTILS | #HASHTAG | N°" de la référence */}
        <RunningBar label={label} hashtag={hashtag} issueLabel={issueLabel} variant="footer" />
      </div>
    );
  }

  /* ── NEWSLETTER PROMO — approximation de la page abonnement ── */
  if (block.type === 'newsletter-promo') {
    return (
      <div className="journal-newsletter journal-feature-photo" style={{ margin: '0 -48px', position: 'relative', height: block.height === 'large' ? '480px' : '380px', overflow: 'hidden' }}>
        {block.photoUrl && <img src={block.photoUrl} alt={block.photoAlt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,23,42,0.6) 0%, rgba(15,23,42,0.15) 45%, rgba(15,23,42,0) 60%)' }} />

        <div style={{ position: 'absolute', left: 0, right: 0, bottom: '76px', textAlign: 'center', padding: '0 32px' }}>
          {block.intro && <p style={{ margin: 0, color: '#fff', fontSize: '1.05rem', lineHeight: 1.35, fontFamily: SANS, whiteSpace: 'pre-line' }}>{block.intro}</p>}
          {block.highlight && <p style={{ margin: '4px 0 14px', color: '#fff', fontWeight: 900, fontSize: '1.8rem', fontFamily: SANS, letterSpacing: '-0.01em' }}>{block.highlight}</p>}
          {(block.ctaText || block.ctaEmail) && (
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.92)', fontSize: '0.76rem', fontFamily: SANS }}>
              {block.ctaText && <strong>{block.ctaText} </strong>}{block.ctaEmail}
            </p>
          )}
        </div>

        {/* Bandeau bas — réseaux sociaux + handle + déco */}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: accent, padding: '14px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {block.instagram && <SocialCircle><FaInstagram /></SocialCircle>}
            {block.linkedin  && <SocialCircle><FaLinkedinIn /></SocialCircle>}
            {block.facebook  && <SocialCircle><FaFacebookF /></SocialCircle>}
            {block.socialHandle && <span style={{ color: '#fff', fontWeight: 800, fontSize: '0.68rem', marginLeft: '6px', fontFamily: SANS, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{block.socialHandle}</span>}
          </div>
          <DotGrid />
          <AvatarRow />
        </div>
      </div>
    );
  }

  /* ── BANNER ── */
  if (block.type === 'banner') {
    const bg = block.backgroundColor || accent;
    const fg = block.textColor || '#ffffff';
    return (
      <div className="journal-banner" style={{ margin: '0 -48px', padding: '20px 48px', background: bg, textAlign: 'center' }}>
        {block.title    && <h2 style={{ margin: '0 0 4px', color: fg, fontFamily: SANS, fontSize: '1.4rem', fontWeight: 900, letterSpacing: '0.01em', textTransform: 'uppercase' }}>{block.title}</h2>}
        {block.subtitle && <p  style={{ margin: 0, color: fg, opacity: 0.85, fontSize: '0.85rem', fontFamily: SANS }}>{block.subtitle}</p>}
      </div>
    );
  }

  /* ── SECTION ── */
  if (block.type === 'section') {
    return (
      <div className="journal-section" style={{ margin: 0 }}>
        <div style={{ margin: '0 -48px', background: accent, display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 48px' }}>
          <div style={{ width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: '9px solid rgba(255,255,255,0.45)', flexShrink: 0 }} />
          <h2 style={{ margin: 0, fontSize: '0.74rem', fontWeight: 900, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: SANS }}>
            {block.title || ''}
          </h2>
        </div>
        {block.body && (
          <div style={{ padding: '18px 0 0' }}>
            <div className="ql-editor journal-body" style={bodyStyle(columns)} dangerouslySetInnerHTML={safe(block.body)} />
          </div>
        )}
      </div>
    );
  }

  /* ── MAIN ARTICLE ── */
  if (block.type === 'main-article') {
    return (
      <div className="journal-article" style={{ margin: 0 }}>
        {block.headline && (
          <div style={{ marginBottom: '16px', paddingBottom: '14px', borderBottom: `3px solid ${accent}` }}>
            <h2 style={{ margin: 0, fontFamily: SANS, fontSize: '1.4rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.25, textAlign: 'center', textTransform: 'uppercase' }}>
              {block.headline}
            </h2>
          </div>
        )}
        {block.body && (
          <div className="ql-editor journal-body" style={bodyStyle(columns)} dangerouslySetInnerHTML={safe(block.body)} />
        )}
      </div>
    );
  }

  /* ── QUOTE ── */
  if (block.type === 'quote') {
    return (
      <div className="journal-quote" style={{ margin: 0, padding: '20px 28px', background: tint, borderLeft: `5px solid ${accent}`, position: 'relative' }}>
        <div style={{ position: 'absolute', top: '6px', left: '14px', fontSize: '3.2rem', lineHeight: 1, color: accent, opacity: 0.18, fontFamily: SANS, userSelect: 'none' }}>"</div>
        <p style={{ margin: '0 0 12px', fontSize: '1rem', color: '#1e293b', lineHeight: 1.7, fontFamily: SANS, fontWeight: 500, position: 'relative' }}>{block.text || ''}</p>
        {block.author && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '2px', background: accent }} />
            <cite style={{ fontSize: '0.68rem', color: accent, fontStyle: 'normal', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: SANS }}>{block.author}</cite>
          </div>
        )}
      </div>
    );
  }

  /* ── PULL QUOTE ── */
  if (block.type === 'pull-quote') {
    return (
      <div className="journal-pull-quote" style={{ margin: '0 -48px', padding: '28px 48px', textAlign: 'center', background: tint, borderTop: `3px solid ${accent}`, borderBottom: `3px solid ${accent}` }}>
        <p style={{ margin: '0 0 14px', fontSize: '1.25rem', color: '#0f172a', lineHeight: 1.5, fontFamily: SANS, fontWeight: 700 }}>{block.text || ''}</p>
        {block.author && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '24px', height: '2px', background: accent }} />
            <cite style={{ fontSize: '0.68rem', color: accent, fontStyle: 'normal', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: SANS }}>{block.author}</cite>
            <div style={{ width: '24px', height: '2px', background: accent }} />
          </div>
        )}
      </div>
    );
  }

  /* ── STATS ROW ── */
  if (block.type === 'stats-row') {
    const stats = Array.isArray(block.stats) ? block.stats : [];
    return (
      <div className="journal-stats" style={{ margin: '0 -48px', background: tint, display: 'flex' }}>
        {stats.map((s, i) => (
          <div key={i} style={{ flex: '1 1 0', textAlign: 'center', padding: '20px 8px', borderRight: i < stats.length - 1 ? `1px solid ${hexTint(accent, 0.3)}` : 'none' }}>
            <div style={{ fontSize: '2rem', fontWeight: 900, color: accent, fontFamily: SANS, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: SANS, marginTop: '5px', lineHeight: 1.3 }}>{s.label}</div>
          </div>
        ))}
      </div>
    );
  }

  /* ── HIGHLIGHT ── */
  if (block.type === 'highlight') {
    return (
      <div className="journal-highlight" style={{ margin: 0, padding: '16px 20px', background: tint, borderLeft: `4px solid ${accent}` }}>
        {block.title && <div style={{ fontSize: '0.7rem', fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: SANS, marginBottom: '8px' }}>{block.title}</div>}
        <div className="ql-editor journal-body" style={{ fontSize: '0.85rem', lineHeight: 1.75, color: '#1e293b', fontFamily: SANS, padding: 0 }} dangerouslySetInnerHTML={safe(block.body)} />
      </div>
    );
  }

  /* ── CALLOUT ── */
  if (block.type === 'callout') {
    const cs = CALLOUT_STYLES[block.style] || CALLOUT_STYLES.info;
    return (
      <div className="journal-callout" style={{ margin: 0, padding: '14px 18px', background: cs.bg, borderLeft: `4px solid ${cs.border}`, display: 'flex', gap: '12px' }}>
        <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '1px' }}>{cs.icon}</span>
        <div>
          <div style={{ fontSize: '0.65rem', fontWeight: 800, color: cs.border, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: SANS, marginBottom: '4px' }}>{block.title || cs.label}</div>
          <div className="ql-editor journal-body" style={{ fontSize: '0.82rem', lineHeight: 1.7, color: '#1e293b', fontFamily: SANS, padding: 0 }} dangerouslySetInnerHTML={safe(block.body)} />
        </div>
      </div>
    );
  }

  /* ── TWO COLUMNS ── */
  if (block.type === 'two-columns') {
    const colHeader = (t) => t ? (
      <div style={{ fontSize: '0.65rem', fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: SANS, marginBottom: '10px', paddingBottom: '6px', borderBottom: `2px solid ${accent}` }}>{t}</div>
    ) : null;
    return (
      <div className="journal-two-columns" style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 2.4rem', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ paddingTop: '16px' }}>
          {colHeader(block.leftTitle)}
          <div className="ql-editor journal-body" style={{ fontSize: '0.85rem', lineHeight: 1.8, color: '#1e293b', fontFamily: SANS, padding: 0, textAlign: 'justify' }} dangerouslySetInnerHTML={safe(block.leftBody)} />
        </div>
        <div style={{ paddingTop: '16px', borderLeft: '1px solid #e5e7eb', paddingLeft: '2.4rem' }}>
          {colHeader(block.rightTitle)}
          <div className="ql-editor journal-body" style={{ fontSize: '0.85rem', lineHeight: 1.8, color: '#1e293b', fontFamily: SANS, padding: 0, textAlign: 'justify' }} dangerouslySetInnerHTML={safe(block.rightBody)} />
        </div>
      </div>
    );
  }

  /* ── TEAM MEMBER ── */
  if (block.type === 'team-member') {
    return (
      <div className="journal-team-member" style={{ margin: 0, display: 'flex', gap: '16px', alignItems: 'flex-start', padding: '16px', background: hexTint(accent, 0.07), borderTop: `3px solid ${accent}` }}>
        {block.photoUrl
          ? <img src={block.photoUrl} alt={block.name} style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${accent}` }} />
          : block.name && <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', fontSize: '1.4rem', fontWeight: 900, fontFamily: SANS }}>{block.name.charAt(0)}</div>
        }
        <div style={{ flex: 1 }}>
          {block.name  && <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a', fontFamily: SANS, marginBottom: '2px' }}>{block.name}</div>}
          {block.role  && <div style={{ fontSize: '0.65rem', color: accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: SANS, marginBottom: '8px' }}>{block.role}</div>}
          {block.bio   && <div style={{ fontSize: '0.82rem', color: '#475569', fontFamily: SANS, lineHeight: 1.65, marginBottom: block.quote ? '8px' : 0 }}>{block.bio}</div>}
          {block.quote && <div style={{ fontSize: '0.78rem', color: '#334155', fontFamily: SANS, padding: '6px 10px', borderLeft: `3px solid ${accent}`, background: hexTint(accent, 0.08) }}>"{block.quote}"</div>}
        </div>
      </div>
    );
  }

  /* ── CONTACT ── */
  if (block.type === 'contact') {
    const items = [
      block.phone   && { icon: '📞', label: 'Téléphone', value: block.phone,   href: `tel:${block.phone.replace(/\s/g,'')}` },
      block.email   && { icon: '✉️',  label: 'Email',     value: block.email,   href: `mailto:${block.email}` },
      block.website && { icon: '🌐', label: 'Site',      value: block.website, href: block.website.startsWith('http') ? block.website : `https://${block.website}` },
      block.address && { icon: '📍', label: 'Adresse',   value: block.address, href: null },
    ].filter(Boolean);
    return (
      <div className="journal-contact" style={{ margin: 0, overflow: 'hidden', border: `1px solid ${hexTint(accent, 0.35)}` }}>
        <div style={{ background: accent, padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: '0.9rem', fontFamily: SANS, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{block.title || 'Nous contacter'}</div>
            {block.company && <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.68rem', fontFamily: SANS, marginTop: '2px' }}>{block.company}</div>}
          </div>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            <img src={Logo} alt="HIS" style={{ width: '26px', height: '26px', objectFit: 'contain' }} />
          </div>
        </div>
        {block.tagline && <div style={{ background: tint, padding: '8px 24px', borderBottom: `1px solid ${hexTint(accent, 0.2)}`, fontSize: '0.75rem', color: '#475569', fontFamily: SANS }}>{block.tagline}</div>}
        <div style={{ padding: '14px 24px', display: 'grid', gridTemplateColumns: items.length > 2 ? '1fr 1fr' : '1fr', gap: '10px 24px', background: '#fff' }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: hexTint(accent, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>{item.icon}</div>
              <div>
                <div style={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: SANS, fontWeight: 700 }}>{item.label}</div>
                {item.href
                  ? <a href={item.href} style={{ fontSize: '0.78rem', color: accent, fontFamily: SANS, fontWeight: 600, textDecoration: 'none' }}>{item.value}</a>
                  : <div style={{ fontSize: '0.78rem', color: '#334155', fontFamily: SANS, fontWeight: 600 }}>{item.value}</div>
                }
              </div>
            </div>
          ))}
        </div>
        {block.extra && <div style={{ background: tint, borderTop: `1px solid ${hexTint(accent, 0.2)}`, padding: '8px 24px', fontSize: '0.72rem', color: '#64748b', fontFamily: SANS }}>{block.extra}</div>}
      </div>
    );
  }

  /* ── DIVIDER ── */
  if (block.type === 'divider') {
    return (
      <div className="journal-divider" style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
        <span style={{ color: accent, fontSize: '0.55rem', letterSpacing: '0.5em', fontFamily: SANS }}>✦ ✦ ✦</span>
        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
      </div>
    );
  }

  return null;
}

/* ══════════════════════════════════════════════════════
   SÉQUENCEUR
══════════════════════════════════════════════════════ */
const SPACING = {
  default: 24,
  before_section: 32, after_section: 0,
  before_divider: 20, after_divider: 20,
  before_banner: 28,  after_banner: 20,
  before_stats: 20,   after_stats: 20,
  before_fullbleed: 0, after_fullbleed: 24,
};

const FULL_BLEED_TYPES = ['feature-photo', 'toc-cover', 'section-divider', 'banner', 'pull-quote', 'stats-row'];

function spaceBetween(prev, next) {
  if (!prev) return 0;
  if (FULL_BLEED_TYPES.includes(next)) return SPACING.before_fullbleed;
  if (FULL_BLEED_TYPES.includes(prev)) return SPACING.after_fullbleed;
  if (next === 'section')   return SPACING.before_section;
  if (prev === 'section')   return SPACING.after_section;
  if (next === 'divider')   return SPACING.before_divider;
  if (prev === 'divider')   return SPACING.after_divider;
  return SPACING.default;
}

function BlockList({ blocks, defaultAccent, defaultColumns, meta }) {
  return (
    <>
      {blocks.map((block, i) => {
        const prevType = i > 0 ? blocks[i - 1].type : null;
        const space = spaceBetween(prevType, block.type);
        return (
          <div key={block.id || i} style={{ marginTop: space }}>
            <Block block={block} defaultAccent={defaultAccent} defaultColumns={defaultColumns} meta={meta} />
          </div>
        );
      })}
    </>
  );
}

/* ══════════════════════════════════════════════════════
   COUVERTURE — bandeau fin + bloc couleur titre énorme
══════════════════════════════════════════════════════ */
function CoverHeader({ accent, title, excerpt, issueLabel }) {
  return (
    <div>
      {/* Bandeau fin d'info */}
      <div style={{ background: hexTint(accent, 0.14), display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 48px' }}>
        <span style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#334155', fontFamily: SANS }}>
          Le magazine de Hetep Iaout Services
        </span>
        {issueLabel && (
          <span style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', color: '#334155', fontFamily: SANS }}>{issueLabel}</span>
        )}
      </div>

      {/* Bloc couleur — titre énorme */}
      <div style={{ background: accent, padding: '34px 48px 38px' }}>
        <h1 style={{
          margin: 0,
          fontFamily: SANS,
          fontWeight: 900,
          fontSize: '2.3rem',
          lineHeight: 1.15,
          color: '#ffffff',
          textTransform: 'uppercase',
          letterSpacing: '-0.005em',
        }}>
          {title || 'Journal HIS'}
        </h1>
        {excerpt && (
          <p style={{ margin: '14px 0 0', color: 'rgba(255,255,255,0.82)', fontSize: '0.85rem', fontFamily: SANS, lineHeight: 1.6, maxWidth: '480px' }}>
            {excerpt}
          </p>
        )}
      </div>
    </div>
  );
}

function JournalFooter({ accent, period, hashtag, pageLabel }) {
  return (
    <div style={{ padding: '24px 48px 0' }}>
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.55rem', color: '#94a3b8', fontFamily: SANS, letterSpacing: '0.06em' }}>{pageLabel || ''}</span>
        <span style={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: accent, fontFamily: SANS }}>
          {hashtag || `Fin de l'édition ${period || ''}`}
        </span>
        <span style={{ fontSize: '0.55rem', color: '#94a3b8', fontFamily: SANS }}>hisvie.com</span>
      </div>
      <div style={{ height: '6px', background: accent, marginTop: '12px' }} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
══════════════════════════════════════════════════════ */
export default function JournalRender({
  title, period, excerpt, accentColor = '#274559', columnCount = 2, blocks = [],
  publishedAt, page1Settings, page2Settings, hashtag, issueLabel, runningLabel,
}) {
  const p1Accent  = page1Settings?.accentColor || accentColor;
  const p1Columns = page1Settings?.columnCount || columnCount;
  const p2Accent  = page2Settings?.accentColor || accentColor;
  const p2Columns = page2Settings?.columnCount || columnCount;

  const meta = { hashtag, issueLabel: issueLabel || period, runningLabel: runningLabel || 'Journal HIS' };

  const breakIdx    = blocks.findIndex(b => b.type === 'page-break');
  const page1Blocks = breakIdx >= 0 ? blocks.slice(0, breakIdx)  : blocks;
  const page2Blocks = breakIdx >= 0 ? blocks.slice(breakIdx + 1) : [];
  const hasTwoPages = breakIdx >= 0;

  return (
    <div className="journal-document" style={{ fontFamily: SANS, background: '#fff' }}>

      {/* ══════ PAGE 1 — COUVERTURE ══════ */}
      <div className="journal-page journal-page-1" style={{ background: '#fff', display: 'flex', flexDirection: 'column', aspectRatio: '210 / 297' }}>
        <CoverHeader accent={p1Accent} title={title} excerpt={excerpt} issueLabel={issueLabel || period} />

        <div className="journal-page-content" style={{ padding: '24px 48px', flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
          <BlockList blocks={page1Blocks} defaultAccent={p1Accent} defaultColumns={p1Columns} meta={meta} />
        </div>

        {!hasTwoPages && <JournalFooter accent={p1Accent} period={period} hashtag={hashtag} />}
        {hasTwoPages  && <div style={{ height: '6px', background: p1Accent }} />}
      </div>

      {/* Séparateur web */}
      {hasTwoPages && (
        <div className="no-print" style={{ background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '9px 24px' }}>
          <div style={{ flex: 1, height: '1px', background: '#475569' }} />
          <span style={{ fontSize: '9px', color: '#94a3b8', letterSpacing: '0.18em', textTransform: 'uppercase', fontFamily: SANS, whiteSpace: 'nowrap' }}>↑ Page 1 — Recto &nbsp;|&nbsp; Verso — Page 2 ↓</span>
          <div style={{ flex: 1, height: '1px', background: '#475569' }} />
        </div>
      )}

      {/* ══════ PAGE 2 ══════ */}
      {hasTwoPages && (
        <div className="journal-page journal-page-2" style={{ background: '#fff', display: 'flex', flexDirection: 'column', aspectRatio: '210 / 297' }}>
          <RunningBar label={runningLabel || 'Journal HIS'} hashtag={hashtag} issueLabel={issueLabel || period} variant="header" />
          <div className="journal-page-content" style={{ padding: '24px 48px', flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
            <BlockList blocks={page2Blocks} defaultAccent={p2Accent} defaultColumns={p2Columns} meta={meta} />
          </div>
          <JournalFooter accent={p2Accent} period={period} hashtag={hashtag} />
        </div>
      )}
    </div>
  );
}
